import { foldForMatching } from './text-match';

/**
 * The four partner-name columns, in the two registers the app actually needs.
 *
 * These are never stored. `weddings` holds the parts; every consumer derives
 * the string it wants from this module, the same arrangement `buildWeddingSlug`
 * already uses so the create form can preview what the server will compute.
 */
export interface CoupleNameParts {
  partner1FirstName: string;
  partner1LastName?: string | null;
  partner2FirstName: string;
  partner2LastName?: string | null;
}

function clean(raw: string | null | undefined): string {
  return (raw ?? '').trim();
}

/** Joins the two sides, dropping a blank one rather than leaving a dangling `&`. */
function joinSides(side1: string, side2: string): string {
  return [side1, side2].filter(Boolean).join(' & ');
}

/**
 * `"Emma & Liam"` — the wedding's title in the UI, and the name half of the
 * Gmail label slug.
 *
 * First names, because that is what a planner calls a couple. A dashboard card
 * and a review-queue row have a column of these, and the pair of names the
 * planner has been saying out loud all week is the one they scan for.
 *
 * Adds surnames only to break a tie — two couples with an Alex each are
 * otherwise the same row twice. Surnames that are *also* identical are dropped
 * again: `"Alex Smith & Alex Smith"` says nothing `"Alex & Alex"` does not.
 */
export function coupleShortName(w: CoupleNameParts): string {
  const first1 = clean(w.partner1FirstName);
  const first2 = clean(w.partner2FirstName);
  const firstsOnly = joinSides(first1, first2);

  const collide = first1 && first2 && first1.toLowerCase() === first2.toLowerCase();
  if (!collide) return firstsOnly;

  const last1 = clean(w.partner1LastName);
  const last2 = clean(w.partner2LastName);
  const distinctLast = last1 && last2 && last1.toLowerCase() !== last2.toLowerCase();
  return distinctLast ? joinSides(`${first1} ${last1}`, `${first2} ${last2}`) : firstsOnly;
}

/**
 * `"Emma Smith & Liam Nguyễn"` — for `{{couple_names}}` only, where the reader
 * is a vendor who has never heard of this couple and gets exactly one shot at
 * knowing who they are quoting for.
 */
export function coupleFullName(w: CoupleNameParts): string {
  const side = (first: string, last: string | null | undefined) =>
    [clean(first), clean(last)].filter(Boolean).join(' ');
  return `${side(w.partner1FirstName, w.partner1LastName)} & ${side(
    w.partner2FirstName,
    w.partner2LastName,
  )}`;
}

/**
 * Folded name tokens for the inbound-mail matcher.
 *
 * Deliberately does not use `matchTokens`. That function drops tokens under
 * four characters, a stopword guard that is correct for a blob of free text and
 * wrong here: every token this returns is known to be a name, so `Amy`, `Jon`,
 * `Ng`, and `Kim` are signal rather than noise. The protection that made the
 * four-character floor necessary is already provided downstream by
 * `containsWord`, which matches whole words — `li` will not match `client`.
 *
 * Splits each name on non-alphanumerics before filtering, so a double-barrelled
 * surname like `Smith-Jones` yields `smith` and `jones` separately rather than
 * the single token `smith-jones`, which would only match text written exactly
 * that way.
 */
export function coupleMatchTokens(w: CoupleNameParts): string[] {
  const raw = [w.partner1FirstName, w.partner1LastName, w.partner2FirstName, w.partner2LastName];
  const parts = raw.flatMap((t) => foldForMatching(clean(t)).split(/[^a-z0-9]+/));
  return [...new Set(parts.filter((t) => t.length >= 2))];
}

/**
 * `"A & D"` — the two initials standing in for a couple's portrait.
 *
 * First initials, matching the caption beside it. Spread rather than indexed so
 * a name starting with an astral character — an emoji, or a rarer CJK ideograph
 * — yields that character instead of half of its surrogate pair.
 */
export function coupleMonogram(w: CoupleNameParts): string {
  const initial = (name: string) => [...clean(name)][0]?.toUpperCase() ?? '';
  return joinSides(initial(w.partner1FirstName), initial(w.partner2FirstName));
}

/**
 * What to call a vendor in a greeting: `"Amy"`, not `"Amy Chen"`.
 *
 * An inquiry opens "Hi {{vendor_name}}," — and a planner writing to a florist
 * she has worked with twice says "Hi Amy", not "Hi Amy Chen". The full name is
 * how the rolodex files them; the first name is how the message addresses
 * them, and the template's sample context has always shown the first name as
 * what this variable produces.
 *
 * Only the contact name is cut down. A vendor with nobody named on the record
 * is addressed by the business, whole: `"Bloom & Bower Floral"` reduced to its
 * first word is a different company.
 */
export function vendorGreetingName(
  contactName: string | null | undefined,
  businessName: string | null | undefined,
): string | undefined {
  const contact = clean(contactName);
  if (contact) return contact.split(/\s+/)[0];
  return clean(businessName) || undefined;
}
